import { type Component, ComponentType } from "@/types";
import { BaseCommandHandler, type CommandResult } from "../BaseCommandHandler";

/**
 * Command Handler for Questionnaire components.
 *
 * Handles commands for:
 * - Adding/removing steps
 * - Adding questions to steps
 * - Setting branching rules
 * - View mode switching (form/results)
 * - Layout, accent color, skip, deadline settings
 */
export class QuestionnaireCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private generateId(prefix: string): string {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  }

  private initializePatterns(): void {
    // Add step: "add step", "add a new step", "add step named X"
    this.commandPatterns.set(
      /add (?:a )?(?:new )?step(?:\s+(?:named?|called?|titled?)\s+(.+))?/i,
      (match, component) => {
        const existingSteps = Array.isArray(component.properties?.steps) ? component.properties.steps : [];
        const title = match[1] || `Step ${existingSteps.length + 1}`;
        const newStep = {
          id: this.generateId('step'),
          title,
          order: existingSteps.length,
          questions: [],
        };
        return { properties: { steps: [...existingSteps, newStep] } };
      }
    );

    // Remove step: "remove step 2", "delete last step"
    this.commandPatterns.set(
      /(?:remove|delete) (?:step )?(\d+|last|first)/i,
      (match, component) => {
        const existingSteps = Array.isArray(component.properties?.steps) ? [...component.properties.steps] : [];
        if (existingSteps.length === 0) return { properties: {} };
        let idx: number;
        if (match[1] === 'last') idx = existingSteps.length - 1;
        else if (match[1] === 'first') idx = 0;
        else idx = parseInt(match[1], 10) - 1;
        if (idx >= 0 && idx < existingSteps.length) {
          existingSteps.splice(idx, 1);
          return { properties: { steps: existingSteps.map((s: Record<string, unknown>, i: number) => ({ ...s, order: i })) } };
        }
        return { properties: {} };
      }
    );

    // Add question: "add question to step 1", "add text question"
    this.commandPatterns.set(
      /add (?:a )?(?:(text|number|rating|boolean|date|single.?select|multi.?select) )?question(?:\s+to\s+step\s+(\d+))?/i,
      (match, component) => {
        const existingSteps = Array.isArray(component.properties?.steps) ? [...component.properties.steps] : [];
        if (existingSteps.length === 0) return { properties: {} };
        const type = (match[1] || 'text').toLowerCase().replace(/\s+/g, '-');
        const stepNum = match[2] ? parseInt(match[2], 10) - 1 : existingSteps.length - 1;
        const step = existingSteps[stepNum];
        if (!step) return { properties: {} };
        const questions = Array.isArray(step.questions) ? [...step.questions] : [];
        questions.push({
          id: this.generateId('q'),
          type,
          label: `Question ${questions.length + 1}`,
          required: false,
        });
        existingSteps[stepNum] = { ...step, questions };
        return { properties: { steps: existingSteps } };
      }
    );

    // Switch view: "show results", "switch to results", "show form"
    this.commandPatterns.set(
      /(?:show|switch to|change to) (results?|form)/i,
      (match) => {
        const mode = match[1].toLowerCase().startsWith('result') ? 'results' : 'form';
        return { properties: { viewMode: mode } };
      }
    );

    // Set accent color
    this.commandPatterns.set(
      /set (?:accent )?color (?:to )?([#\w]+)/i,
      (match) => {
        return { properties: { accentColor: match[1] } };
      }
    );

    // Set layout
    this.commandPatterns.set(
      /set layout (?:to )?(stacked|card)/i,
      (match) => {
        return { properties: { questionLayout: match[1].toLowerCase() } };
      }
    );

    // Allow/disable skip
    this.commandPatterns.set(
      /(allow|enable|disable|disallow) skip/i,
      (match) => {
        const allow = match[1].toLowerCase() === 'allow' || match[1].toLowerCase() === 'enable';
        return { properties: { allowSkip: allow } };
      }
    );

    // Set deadline
    this.commandPatterns.set(
      /set deadline (?:to )?(\d{4}-\d{2}-\d{2})/i,
      (match) => {
        return { properties: { responseDeadline: match[1] } };
      }
    );

    // Set branching rule (natural language): "if answer is X go to step Y", "set branching rule"
    this.commandPatterns.set(
      /(?:set branch(?:ing)?(?:\s+rule)?|if (?:the )?answer (?:is|equals?) (.+?) (?:then )?(?:go|skip|jump) to step (\d+))/i,
      (match, component) => {
        const existingSteps = Array.isArray(component.properties?.steps) ? [...component.properties.steps] : [];
        if (existingSteps.length === 0) return { properties: {} };
        // If we captured groups (the "if answer is X go to step Y" form)
        if (match[1] && match[2]) {
          const value = match[1].trim();
          const targetStepIdx = parseInt(match[2], 10) - 1;
          // Apply to the first step that has questions, using its first question
          const sourceStep = existingSteps[0];
          const targetStep = existingSteps[targetStepIdx];
          if (!sourceStep || !targetStep || !sourceStep.questions?.length) return { properties: {} };
          const branchRules = Array.isArray(sourceStep.branchRules) ? [...sourceStep.branchRules] : [];
          branchRules.push({
            id: this.generateId('br'),
            condition: { questionId: sourceStep.questions[0].id, operator: 'equals', value },
            targetStepId: targetStep.id,
          });
          existingSteps[0] = { ...sourceStep, branchRules };
          return { properties: { steps: existingSteps } };
        }
        // Bare "set branching rule" — return no-op with suggestions
        return { properties: {} };
      }
    );

    // Add branching rule (verbose): "add branch from step 1 question q-1 equals yes to step 3"
    this.commandPatterns.set(
      /add branch(?:ing)?\s+(?:from )?step\s+(\d+)\s+(?:question\s+)?(\S+)\s+(equals?|contains?|greater|less)\s+(.+?)\s+to\s+step\s+(\d+)/i,
      (match, component) => {
        const existingSteps = Array.isArray(component.properties?.steps) ? [...component.properties.steps] : [];
        const stepIdx = parseInt(match[1], 10) - 1;
        const questionId = match[2];
        const operator = match[3].toLowerCase().replace(/s$/, '') as 'equal' | 'contain' | 'greater' | 'less';
        const value = match[4].trim();
        const targetStepIdx = parseInt(match[5], 10) - 1;
        const step = existingSteps[stepIdx];
        const targetStep = existingSteps[targetStepIdx];
        if (!step || !targetStep) return { properties: {} };
        const operatorMap: Record<string, string> = { equal: 'equals', contain: 'contains', greater: 'greaterThan', less: 'lessThan' };
        const branchRules = Array.isArray(step.branchRules) ? [...step.branchRules] : [];
        branchRules.push({
          id: this.generateId('br'),
          condition: { questionId, operator: operatorMap[operator] || 'equals', value },
          targetStepId: targetStep.id,
        });
        existingSteps[stepIdx] = { ...step, branchRules };
        return { properties: { steps: existingSteps } };
      }
    );

    // Set submit label
    this.commandPatterns.set(
      /set submit (?:label|text|button) (?:to )?["']?(.+?)["']?$/i,
      (match) => {
        return { properties: { submitLabel: match[1].trim() } };
      }
    );

    // Set title
    this.commandPatterns.set(
      /set (?:questionnaire )?title (?:to )?["']?(.+?)["']?$/i,
      (match) => {
        return { properties: { title: match[1].trim() } };
      }
    );

    // Show/hide progress
    this.commandPatterns.set(
      /(show|hide) progress/i,
      (match) => {
        return { properties: { showProgress: match[1].toLowerCase() === 'show' } };
      }
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.QUESTIONNAIRE) return false;
    const keywords = [
      'step', 'question', 'results', 'form', 'skip', 'deadline',
      'submit', 'progress', 'layout', 'stacked', 'card',
      'accent', 'color', 'title', 'branch', 'view',
    ];
    return keywords.some(kw => command.toLowerCase().includes(kw));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);
    if (changes) {
      return {
        success: true,
        command: {
          type: 'property',
          changes,
          confidence: 0.9,
          description: `Updated questionnaire: ${Object.keys(changes.properties || {}).join(', ')}`,
        },
        suggestions: [],
      };
    }

    return {
      success: false,
      command: {
        type: 'property',
        changes: {},
        confidence: 0,
        description: 'Unrecognized questionnaire command',
      },
      suggestions: [
        'add a new step',
        'add text question to step 1',
        'show results',
        'allow skip',
        'set accent color to #2563eb',
        'set layout to card',
      ],
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      'add a new step named "Demographics"',
      'add text question to step 1',
      'add rating question',
      'remove last step',
      'show results',
      'allow skip',
      'set layout to card',
      'add branch from step 1 question q-1 equals yes to step 3',
    ];
  }

  async getSuggestions(_component: Component): Promise<string[]> {
    return [
      'add a new step',
      'add text question to step 1',
      'show results',
      'set accent color to #2563eb',
      'allow skip',
      'set submit label to "Finish"',
      'add branch from step 1 question q-1 equals yes to step 3',
    ];
  }
}

export const questionnaireCommandHandler = new QuestionnaireCommandHandler();
